'use client'

import { createRef, useEffect, useCallback, useState } from 'react'
import { noop } from 'lodash'

import { Translation } from '@marketplace-web/i18n/i18n-data'
import { InfoBanner } from '@marketplace-web/info-banner/info-banner-feature'

import {
  getLocalStorageItem,
  removeLocalStorageItem,
} from '@marketplace-web/browser/browser-storage-util'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import {
  HelpCenterAutomatedResponseFeedbackModal,
  DENY_AUTOMATED_SUPPORT_FEEDBACK,
  PROVIDE_AUTOMATED_SUPPORT_FEEDBACK,
} from '@marketplace-web/help-center-conversation-support/help-center-automated-feedback-feature'

import ConversationMessages from '../ConversationMessages'
import IntroMessage from '../IntroMessage'
import SuggestedMessages from '../SuggestedMessages'
import ConversationTranslationButton from '../ConversationTranslationButton'
import EscrowBanner from '../EscrowBanner'
import usePostReply from '../../hooks/usePostReply/usePostReply'
import { SUGGESTED_MESSAGES } from './constants'

type Props = {
  toggleSidebar?: () => void
  openOfferModal?: () => void
}

const ConversationContent = ({ toggleSidebar = noop, openOfferModal }: Props) => {
  const { conversation } = useConversationContext()
  const { postReplyRequest } = usePostReply()
  const isWebMessagingSvcFeatureEnabled = useFeatureSwitch('web_messaging_svc')
  const translate = useTranslate()

  const translationType = conversation?.translationType ?? Translation.None

  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false)
  const [isInfoBannerPresent, setIsInfoBannerPresent] = useState<boolean>()

  useEffect(() => {
    const conversationId = conversation?.id.toString()
    const provideFeedbackKey = getLocalStorageItem(PROVIDE_AUTOMATED_SUPPORT_FEEDBACK)
    const denyFeedbackKey = getLocalStorageItem(DENY_AUTOMATED_SUPPORT_FEEDBACK)

    if (provideFeedbackKey === conversationId) {
      const timer = setTimeout(() => {
        setIsFeedbackModalOpen(true)
        removeLocalStorageItem(PROVIDE_AUTOMATED_SUPPORT_FEEDBACK)
      }, 2000)

      return () => clearTimeout(timer)
    }

    if (denyFeedbackKey === conversationId) {
      const timer = setTimeout(() => {
        setIsFeedbackModalOpen(true)
        removeLocalStorageItem(DENY_AUTOMATED_SUPPORT_FEEDBACK)
      }, 5000)

      return () => clearTimeout(timer)
    }

    return undefined
  }, [conversation?.id])

  const contentRef = createRef<HTMLDivElement>()

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (!contentRef.current) return

      contentRef.current.scrollTo(0, contentRef.current.scrollHeight)
    })
  }, [contentRef])

  useEffect(() => {
    scrollToBottom()
  }, [scrollToBottom])

  function renderAutomatedSupportFeedbackModal() {
    if (!isFeedbackModalOpen) return null

    return <HelpCenterAutomatedResponseFeedbackModal isOpen={isFeedbackModalOpen} />
  }

  const renderInfoBanner = () => {
    if (!conversation?.id) return null

    return (
      <InfoBanner
        screen="message_reply"
        params={{ user_msg_thread_id: conversation.id }}
        onBannerLoad={setIsInfoBannerPresent}
      />
    )
  }

  const renderEscrowBanner = () => {
    if (isInfoBannerPresent === false) {
      return <EscrowBanner conversation={conversation} />
    }

    return null
  }

  const renderIntroMessage = () => <IntroMessage />

  const renderConversationMessages = () => (
    <ConversationMessages toggleSidebar={toggleSidebar} openOfferModal={openOfferModal} />
  )

  const renderSuggestedMessagesCore = () => {
    if (!conversation?.suggestedMessages?.length) return null

    return (
      <SuggestedMessages
        messages={conversation.suggestedMessages}
        onMessageClick={value => postReplyRequest({ value })}
        trackingContext={{
          transactionId: conversation.transactionId,
          itemIds: conversation.transaction?.order?.itemIds.toString(),
        }}
      />
    )
  }

  const renderSuggestedMessagesSvcMessaging = () => {
    if (!conversation?.showSuggestedMessagesForSeller) return null

    return (
      <SuggestedMessages
        messages={SUGGESTED_MESSAGES.map((translationKey, index) => ({
          id: index + 1,
          text: translate(translationKey),
        }))}
        onMessageClick={value => postReplyRequest({ value })}
        trackingContext={{
          transactionId: conversation.transactionId,
          // TODO: order.itemIds won't be available in `svc-messaging`
          itemIds: conversation.transaction?.order?.itemIds.toString(),
        }}
      />
    )
  }

  const renderSuggestedMessages = () =>
    isWebMessagingSvcFeatureEnabled
      ? renderSuggestedMessagesSvcMessaging()
      : renderSuggestedMessagesCore()

  const renderTranslationButton = () => {
    const showTranslationButton = translationType !== Translation.None

    if (!showTranslationButton) return null

    return <ConversationTranslationButton />
  }

  return (
    <div className="thread__content" ref={contentRef} data-testid="conversation-content">
      <div className="u-ui-margin-top-large">
        {renderInfoBanner()}
        {renderEscrowBanner()}
        {renderIntroMessage()}
        {renderConversationMessages()}
        {renderSuggestedMessages()}
        {renderTranslationButton()}
        {renderAutomatedSupportFeedbackModal()}
      </div>
    </div>
  )
}

export default ConversationContent
